import { Save, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { ContractStatusBadge } from '@/components/shared/StatusBadges'
import { Permit } from '@/components/guards/Permit'
import { ContractStatusSelect } from '@/components/contracts/ContractStatusSelect'
import type { ContractStatus } from '@/types'
import { cn } from '@/lib/utils'
import { outlineBtnClass, primaryBtnClass } from './styles'

const headerOutlineBtnClass = cn(
  outlineBtnClass,
  'text-sm normal-case hover:bg-[#fbfbfc]'
)

const headerPrimaryBtnClass = cn(primaryBtnClass, 'text-sm normal-case')

const headerStatusBadgeClass = 'py-0 text-sm normal-case'

interface Props {
  formStatus: ContractStatus
  editing: boolean
  saving: boolean
  contractId?: string
  contractNumber?: string | null
  onCancel: () => void
  onSaveDraft: () => void
  onSaveAndSend: () => void
  onTerminate?: () => void
}

export function ContractFormHeader({
  formStatus,
  editing,
  saving,
  contractId,
  contractNumber,
  onCancel,
  onSaveDraft,
  onSaveAndSend,
  onTerminate,
}: Props) {
  const { t } = useTranslation('contracts')
  const canUpdateStatus = editing && contractId && onTerminate

  return (
    <header className='sticky top-0 z-20 -mx-6 -mt-6 border-b border-[#e7e9ef] bg-white px-16 py-4 shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]'>
      <div className='flex flex-wrap items-center gap-6'>
        <div className='flex min-w-0 flex-1 flex-wrap items-center gap-3'>
          {editing && (
            <h1 className='min-w-0 truncate font-heading text-2xl font-semibold leading-[1.2] tracking-[-0.48px] text-[#171a20]'>
              {contractNumber}
            </h1>
          )}
          {canUpdateStatus ? (
            <Permit
              module='contracts'
              action='edit'
              fallback={
                <ContractStatusBadge
                  status={formStatus}
                  size='md'
                  className={headerStatusBadgeClass}
                />
              }
            >
              <ContractStatusSelect
                contractId={contractId}
                currentStatus={formStatus}
                onTerminate={onTerminate}
                size='md'
                triggerClassName='h-8 rounded-lg px-2 py-0 text-sm normal-case leading-[1.4]'
              />
            </Permit>
          ) : (
            <ContractStatusBadge
              status={formStatus}
              size='md'
              className={headerStatusBadgeClass}
            />
          )}
        </div>
        <div className='flex flex-wrap items-center gap-3'>
          <Button
            type='button'
            variant='outline'
            className={headerOutlineBtnClass}
            onClick={onCancel}
          >
            {t('form.actions.cancel')}
          </Button>
          <Button
            type='button'
            variant='outline'
            className={headerOutlineBtnClass}
            disabled={saving}
            onClick={onSaveDraft}
          >
            <Save className='size-4' />
            {editing
              ? t('form.actions.saveChanges')
              : t('form.actions.saveAsDraft')}
          </Button>
          <Button
            type='button'
            className={headerPrimaryBtnClass}
            disabled={saving}
            onClick={onSaveAndSend}
          >
            <Send className='size-4' />
            {t('form.actions.sendToClient')}
          </Button>
        </div>
      </div>
    </header>
  )
}
